import React from 'react';
import { PaymentElement, ExpressCheckoutElement } from '@stripe/react-stripe-js';
import { FiCreditCard } from 'react-icons/fi';
import logger from '../../../utils/logging/logger';
import styles from '../DonationForm.module.css';

// The payment section: the express-checkout + Stripe PaymentElement (shown once
// a clientSecret/valid-monthly-amount exists), the donation summary box, and the
// two-stage submit ('Proceed to Payment' -> 'Donate $X'). Extracted VERBATIM
// from DonationForm's render (three sibling top-level JSX expressions, hence
// the wrapping fragment).
export default function DonationPaymentSection({
  clientSecretFromParent, clientSecret, creatingPaymentIntent, donationType, elements,
  email, firstName, formatCurrency, handleProceedToPayment, hasExpressCheckout,
  isForever, lastName, loading, monthlyAmount, navigate,
  numberOfPayments, oneTimeAmount, preservedIsForever, preservedMonthlyAmount, preservedNumberOfPayments,
  preservedOneTimeAmount, setError, setHasExpressCheckout, showPaymentElement, showPaymentForm,
  stripe, totalAmount, user,
}) {
  return (
    <>
            {showPaymentForm && (() => {
              // For one-time donations: show if we have clientSecret (from parent or local) and showPaymentElement is true
              // For monthly donations: 
              //   - Forever subscriptions: show if we have clientSecret (created by subscription)
              //   - Limited subscriptions: show if we have valid amount/payments for PaymentMethod creation
              const hasClientSecret = !!(clientSecret || clientSecretFromParent);
              const shouldShowOneTime = showPaymentElement && hasClientSecret && donationType === 'one-time';
              
              // For forever subscriptions, show payment element if we have a clientSecret
              const shouldShowMonthlyForever = donationType === 'monthly' && (isForever || preservedIsForever) && 
                hasClientSecret && showPaymentElement;
              
              // For limited monthly subscriptions, show if we have valid amount/payments
              const shouldShowMonthlyLimited = donationType === 'monthly' && !isForever && !preservedIsForever && 
                (monthlyAmount || preservedMonthlyAmount) && 
                (numberOfPayments || preservedNumberOfPayments) &&
                parseInt(numberOfPayments || preservedNumberOfPayments) >= 1;
              
              const shouldShow = shouldShowOneTime || shouldShowMonthlyForever || shouldShowMonthlyLimited;
              
              return shouldShow;
            })() && (
              <div className={styles.formSection}>
                <h4 className={styles.sectionTitle}>
                  <FiCreditCard /> Payment Information
                </h4>
    
                <div className={styles.paymentElementContainer}>
                  {clientSecret && (
                    <div className={styles.expressCheckoutWrapper}>
                      <ExpressCheckoutElement
                        options={{
                          buttonType: {
                            applePay: 'donate',
                            googlePay: 'donate',
                            paypal: 'pay'
                          },
                          buttonHeight: 48,
                          buttonTheme: {
                            applePay: 'black',
                            googlePay: 'black',
                            paypal: 'gold'
                          },
                          layout: {
                            overflow: 'never'
                          },
                          paymentMethods: {
                            applePay: 'auto',
                            googlePay: 'auto',
                            paypal: 'auto',
                            link: 'auto'
                          }
                        }}
                        onConfirm={async () => {
                          try {
                            setError('');
                            const { error: confirmError } = await stripe.confirmPayment({
                              elements,
                              clientSecret,
                              confirmParams: {
                                return_url: window.location.origin + '/portal/donation-confirmation'
                              },
                              redirect: 'if_required'
                            });
                            if (confirmError) {
                              setError(confirmError.message || 'Payment failed. Please try again.');
                            } else {
                              navigate('/donation-confirmation', {
                                state: {
                                  donationData: {
                                    donationType,
                                    oneTimeAmount: preservedOneTimeAmount || oneTimeAmount,
                                    monthlyAmount: preservedMonthlyAmount || monthlyAmount,
                                    numberOfPayments: preservedIsForever || isForever ? 'forever' : (preservedNumberOfPayments || numberOfPayments),
                                    isForever: preservedIsForever || isForever,
                                    user: user || { firstName, lastName, email },
                                    paymentIntentId: clientSecret.split('_secret_')[0]
                                  }
                                }
                              });
                            }
                          } catch (err) {
                            logger.error('Express checkout confirm error', { error: err });
                            setError(err.message || 'Payment failed. Please try again.');
                          }
                        }}
                        onReady={(event) => {
                          const available = event?.availablePaymentMethods || {};
                          setHasExpressCheckout(Boolean(available.applePay || available.googlePay || available.paypal || available.link));
                        }}
                        onError={(event) => {
                          logger.error('Express checkout error', { error: event?.error?.message || event });
                          setError(event?.error?.message || 'Wallet payment failed. Please try another method.');
                        }}
                      />
                      {hasExpressCheckout && (
                        <div className={styles.orPayDivider}>
                          <span>or pay with card</span>
                        </div>
                      )}
                    </div>
                  )}
    
                  <PaymentElement
                    options={{
                      wallets: {
                        googlePay: 'never',
                        applePay: 'never'
                      },
                      layout: {
                        type: 'accordion',
                        defaultCollapsed: false
                      }
                    }}
                  />
                </div>
              </div>
            )}
    
            {((donationType === 'one-time' && (oneTimeAmount || preservedOneTimeAmount)) || (donationType === 'monthly' && (monthlyAmount || preservedMonthlyAmount) && ((numberOfPayments || preservedNumberOfPayments) || isForever || preservedIsForever))) && (
              <div className={styles.summaryBox}>
                {donationType === 'one-time' ? (
                  <div className={`${styles.summaryRow} ${styles.summaryTotalNoBorder}`}>
                    <span className={styles.summaryLabel}>Total Amount:</span>
                    <span className={styles.summaryValue}>{formatCurrency(preservedOneTimeAmount || totalAmount)}</span>
                  </div>
                ) : (
                  <>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Monthly Amount:</span>
                      <span className={styles.summaryValue}>{formatCurrency(preservedMonthlyAmount || monthlyAmount)}</span>
                    </div>
                    <div className={styles.summaryRow}>
                      <span className={styles.summaryLabel}>Duration:</span>
                      <span className={styles.summaryValue}>{(preservedIsForever || isForever) ? 'Unlimited' : `${preservedNumberOfPayments || numberOfPayments} months`}</span>
                    </div>
                    {!(preservedIsForever || isForever) && (
                      <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                        <span className={styles.summaryLabel}>Total Amount:</span>
                        <span className={styles.summaryValue}>{formatCurrency(
                          preservedMonthlyAmount && preservedNumberOfPayments 
                            ? preservedMonthlyAmount * (typeof preservedNumberOfPayments === 'number' ? preservedNumberOfPayments : parseInt(preservedNumberOfPayments))
                            : totalAmount
                        )}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
    
            {!showPaymentForm ? (
              <button
                className={styles.submitButton}
                type="button"
                onClick={handleProceedToPayment}
                disabled={creatingPaymentIntent || loading || 
                  (donationType === 'one-time' && (!oneTimeAmount || parseFloat(oneTimeAmount) < 0.01)) ||
                  (donationType === 'monthly' && (!monthlyAmount || parseFloat(monthlyAmount) < 0.01 || (!isForever && (!numberOfPayments || numberOfPayments < 1 || numberOfPayments > 120))))}
                style={{ width: '100%' }}
              >
                {creatingPaymentIntent ? (
                  <>Creating secure payment...</>
                ) : (
                  <>
                    <FiCreditCard style={{ marginRight: '0.5rem' }} />
                    Proceed to Payment
                  </>
                )}
              </button>
            ) : (
              <button
                className={styles.submitButton} type="submit" disabled={loading || !stripe || (showPaymentElement && !elements)}>
                {loading ? (
                  'Processing...'
                ) : (
                  <>
                    <FiCreditCard size={16} />
                    {(() => {
                      const isMonthlyForever = donationType === 'monthly' && (preservedIsForever || isForever);
                      const amount = donationType === 'monthly'
                        ? (preservedMonthlyAmount || parseFloat(monthlyAmount) || 0)
                        : (preservedOneTimeAmount || parseFloat(oneTimeAmount) || totalAmount || 0);
                      if (isMonthlyForever && amount) {
                        return `Donate ${formatCurrency(amount)}/month`;
                      }
                      if (donationType === 'monthly' && amount) {
                        return `Donate ${formatCurrency(amount)}/month`;
                      }
                      if (amount) {
                        return `Donate ${formatCurrency(amount)}`;
                      }
                      return 'Donate';
                    })()}
                  </>
                )}
              </button>
            )}
    </>
  );
}
